import { ProviderRpcError, RpcErrorCode } from "./types.js";
/**
 * Selects the chain for a namespace.
 *
 * An explicitly targeted chain wins, followed by the active chain and then the first configured
 * chain in the namespace.
 *
 * @returns A CAIP-2 chain ID, or `undefined` when the provider has no chain in that namespace.
 */
export function resolveChainId(req, ctx, namespace) {
    if (req.chainId?.startsWith(`${namespace}:`))
        return req.chainId;
    return ctx.activeChainId(namespace) ?? ctx.chains.find((c) => c.namespace === namespace)?.id;
}
/**
 * A wallet may approve fewer methods than were proposed. Sending one it declined gets an opaque
 * wallet-side error, so refuse locally instead. A namespace the session does not describe fails
 * open: the wallet is the authority on what it accepted.
 */
export function requireApprovedMethod(ctx, namespace, method) {
    const approved = ctx.session()?.namespaces[namespace]?.methods;
    if (!approved || approved.includes(method))
        return;
    throw new ProviderRpcError(RpcErrorCode.unsupportedMethod, `The wallet did not approve "${method}" for ${namespace}. It approved: ${approved.join(", ") || "nothing"}.`);
}
export function flattenChains(chains) {
    const out = [];
    for (const c of chains) {
        if ("adapter" in c)
            out.push(c);
        else
            out.push(...c);
    }
    return out;
}
export function uniqueAdapters(chains) {
    const seen = new Set();
    const out = [];
    for (const c of chains) {
        if (seen.has(c.adapter))
            continue;
        seen.add(c.adapter);
        out.push(c.adapter);
    }
    return out;
}
export function proposeNamespaces(chains) {
    const map = new Map();
    for (const c of chains) {
        let g = map.get(c.namespace);
        if (!g) {
            g = { chains: [], methods: new Set(c.adapter.methods), events: new Set(c.adapter.events) };
            map.set(c.namespace, g);
        }
        g.chains.push(c.id);
        for (const m of c.adapter.methods)
            g.methods.add(m);
        for (const e of c.adapter.events)
            g.events.add(e);
    }
    return Object.fromEntries([...map].map(([ns, g]) => [ns, { chains: g.chains, methods: [...g.methods], events: [...g.events] }]));
}
export async function applyProposal(features, proposal) {
    let next = proposal;
    for (const f of features) {
        const out = await f.onProposal?.(next);
        if (out)
            next = out;
    }
    return next;
}
//# sourceMappingURL=plugin.js.map